/**
 * GraphQL Queries and Mutations
 * Defines all GraphQL operations for the RideQuest API
 */

// ============================================
// Authentication Mutations
// ============================================

export const REGISTER_MUTATION = `
  mutation Register($input: RegisterInput!) {
    register(input: $input) {
      token
      refreshToken
      user {
        id
        email
        fullName
        role
        phoneNumber
        serviceProviderId
        isActive
        createdAt
        updatedAt
      }
    }
  }
`;

export const LOGIN_MUTATION = `
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      token
      refreshToken
      user {
        id
        email
        fullName
        role
        phoneNumber
        serviceProviderId
        isActive
        createdAt
        updatedAt
      }
    }
  }
`;

export const REFRESH_TOKEN_MUTATION = `
  mutation RefreshToken($token: String!, $refreshToken: String!) {
    refreshToken(token: $token, refreshToken: $refreshToken) {
      token
      refreshToken
      user {
        id
        email
        fullName
        role
      }
    }
  }
`;

export const CHANGE_PASSWORD_MUTATION = `
  mutation ChangePassword($input: ChangePasswordInput!) {
    changePassword(input: $input) {
      success
      message
    }
  }
`;

export const REQUEST_PASSWORD_RESET_MUTATION = `
  mutation RequestPasswordReset($input: RequestPasswordResetInput!) {
    requestPasswordReset(input: $input) {
      success
      message
    }
  }
`;

export const RESET_PASSWORD_MUTATION = `
  mutation ResetPassword($input: ResetPasswordInput!) {
    resetPassword(input: $input) {
      success
      message
    }
  }
`;

export const REQUEST_MAGIC_LINK_MUTATION = `
  mutation RequestMagicLink($input: RequestMagicLinkInput!) {
    requestMagicLink(input: $input) {
      success
      message
    }
  }
`;

export const VERIFY_MAGIC_LINK_MUTATION = `
  mutation VerifyMagicLink($input: VerifyMagicLinkInput!) {
    verifyMagicLink(input: $input) {
      token
      refreshToken
      user {
        id
        email
        fullName
        role
        phoneNumber
        serviceProviderId
        isActive
        createdAt
        updatedAt
      }
    }
  }
`;

// ============================================
// User Queries
// ============================================

export const ME_QUERY = `
  query Me {
    me {
      id
      email
      fullName
      role
      phoneNumber
      serviceProviderId
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const ALL_USERS_QUERY = `
  query AllUsers {
    users {
      id
      email
      fullName
      role
      phoneNumber
      serviceProviderId
      isActive
      createdAt
      updatedAt
    }
  }
`;

// ============================================
// Booking Queries
// ============================================

export const MY_BOOKINGS_QUERY = `
  query MyBookings {
    myBookings {
      id
      bookingNumber
      pickupLocation
      dropoffLocation
      pickupDate
      pickupTime
      pickupType
      passengerName
      companyName
      countryCode
      passengerMobile
      passengerEmail
      paymentMethod
      communicationPrefs {
        voice
        sms
        whatsapp
        email
      }
      passengers
      luggage
      selectedRide
      estimatedDuration
      status
      statusReason
      bookingDetails
      isVerified
      isAnonymousBooking
      userId
      serviceProviderId
      serviceProvider {
        id
        serviceProviderName
      }
      createdAt
      updatedAt
    }
  }
`;

export const BOOKING_BY_ID_QUERY = `
  query BookingById($id: UUID!) {
    bookingById(id: $id) {
      id
      bookingNumber
      pickupLocation
      dropoffLocation
      pickupDate
      pickupTime
      pickupType
      passengerName
      companyName
      countryCode
      passengerMobile
      passengerEmail
      paymentMethod
      communicationPrefs {
        voice
        sms
        whatsapp
        email
      }
      passengers
      luggage
      selectedRide
      estimatedDuration
      status
      statusReason
      bookingDetails
      isVerified
      isAnonymousBooking
      userId
      serviceProviderId
      serviceProvider {
        id
        serviceProviderName
      }
      createdAt
      updatedAt
    }
  }
`;

export const BOOKINGS_BY_SERVICE_PROVIDER_QUERY = `
  query BookingsByServiceProvider($serviceProviderId: UUID!) {
    bookingsByServiceProvider(serviceProviderId: $serviceProviderId) {
      id
      bookingNumber
      pickupLocation
      dropoffLocation
      pickupDate
      pickupTime
      pickupType
      passengerName
      companyName
      countryCode
      passengerMobile
      passengerEmail
      paymentMethod
      communicationPrefs {
        voice
        sms
        whatsapp
        email
      }
      passengers
      luggage
      selectedRide
      estimatedDuration
      status
      statusReason
      bookingDetails
      isVerified
      isAnonymousBooking
      userId
      serviceProviderId
      serviceProvider {
        id
        serviceProviderName
      }
      createdAt
      updatedAt
    }
  }
`;

export const ALL_BOOKINGS_QUERY = `
  query AllBookings {
    bookings(order: { createdAt: DESC }) {
      nodes {
        id
        bookingNumber
        pickupLocation
        dropoffLocation
        pickupDate
        pickupTime
        pickupType
        passengerName
        companyName
        countryCode
        passengerMobile
        passengerEmail
        paymentMethod
        communicationPrefs {
          voice
          sms
          whatsapp
          email
        }
        passengers
        luggage
        selectedRide
        estimatedDuration
        status
        statusReason
        bookingDetails
        isVerified
        isAnonymousBooking
        userId
        serviceProviderId
        serviceProvider {
          id
          serviceProviderName
        }
        createdAt
        updatedAt
      }
    }
  }
`;

// ============================================
// Booking Mutations
// ============================================

export const CREATE_BOOKING_MUTATION = `
  mutation CreateBooking($input: CreateBookingInput!) {
    createBooking(input: $input) {
      booking {
        id
        bookingNumber
        pickupLocation
        dropoffLocation
        pickupDate
        pickupTime
        pickupType
        passengerName
        passengerMobile
        passengerEmail
        status
        isAnonymousBooking
        createdAt
      }
      success
      message
      accountCreated
      generatedPassword
      userEmail
    }
  }
`;

export const UPDATE_BOOKING_STATUS_MUTATION = `
  mutation UpdateBookingStatus($input: UpdateBookingStatusInput!) {
    updateBookingStatus(input: $input) {
      booking {
        id
        bookingNumber
        pickupLocation
        dropoffLocation
        pickupDate
        pickupTime
        pickupType
        passengerName
        companyName
        countryCode
        passengerMobile
        passengerEmail
        paymentMethod
        passengers
        luggage
        selectedRide
        estimatedDuration
        status
        statusReason
        bookingDetails
        isVerified
        isAnonymousBooking
        userId
        serviceProviderId
        createdAt
        updatedAt
      }
    }
  }
`;

export const CONFIRM_ANONYMOUS_BOOKING_MUTATION = `
  mutation ConfirmAnonymousBooking($bookingId: UUID!) {
    confirmAnonymousBooking(bookingId: $bookingId) {
      booking {
        id
        bookingNumber
        pickupLocation
        dropoffLocation
        pickupDate
        pickupTime
        pickupType
        passengerName
        companyName
        countryCode
        passengerMobile
        passengerEmail
        paymentMethod
        passengers
        luggage
        selectedRide
        estimatedDuration
        status
        statusReason
        bookingDetails
        isVerified
        isAnonymousBooking
        userId
        serviceProviderId
        createdAt
        updatedAt
      }
      success
      message
    }
  }
`;

export const CONFIRM_MY_BOOKING_MUTATION = `
  mutation ConfirmMyBooking($bookingId: UUID!) {
    confirmMyBooking(bookingId: $bookingId) {
      booking {
        id
        bookingNumber
        pickupLocation
        dropoffLocation
        pickupDate
        pickupTime
        pickupType
        passengerName
        companyName
        countryCode
        passengerMobile
        passengerEmail
        paymentMethod
        passengers
        luggage
        selectedRide
        estimatedDuration
        status
        statusReason
        bookingDetails
        isVerified
        isAnonymousBooking
        userId
        serviceProviderId
        createdAt
        updatedAt
      }
      success
      message
    }
  }
`;

export const CANCEL_MY_BOOKING_MUTATION = `
  mutation CancelMyBooking($bookingId: UUID!, $reason: String!) {
    cancelMyBooking(bookingId: $bookingId, reason: $reason) {
      booking {
        id
        bookingNumber
        pickupLocation
        dropoffLocation
        pickupDate
        pickupTime
        pickupType
        passengerName
        companyName
        countryCode
        passengerMobile
        passengerEmail
        paymentMethod
        passengers
        luggage
        selectedRide
        estimatedDuration
        status
        statusReason
        bookingDetails
        isVerified
        isAnonymousBooking
        userId
        serviceProviderId
        createdAt
        updatedAt
      }
      success
      message
    }
  }
`;

export const CANCEL_BOOKING_MUTATION = `
  mutation CancelBooking($bookingId: UUID!, $reason: String!) {
    cancelBooking(bookingId: $bookingId, reason: $reason) {
      booking {
        id
        bookingNumber
        pickupLocation
        dropoffLocation
        pickupDate
        pickupTime
        pickupType
        passengerName
        companyName
        countryCode
        passengerMobile
        passengerEmail
        paymentMethod
        passengers
        luggage
        selectedRide
        estimatedDuration
        status
        statusReason
        bookingDetails
        isVerified
        isAnonymousBooking
        userId
        serviceProviderId
        createdAt
        updatedAt
      }
    }
  }
`;

// ============================================
// Service Provider Queries
// ============================================

export const SERVICE_PROVIDERS_QUERY = `
  query ServiceProviders($includeInactive: Boolean) {
    serviceProviders(includeInactive: $includeInactive) {
      id
      serviceProviderName
      serviceKey
      contactEmail
      contactPhone
      primaryColor
      logoUrl
      isActive
      maxDistance
      defaultLocation {
        lat
        lng
      }
      defaultZoom
      timezone
      attributesJson
      createdAt
      updatedAt
    }
  }
`;

export const SERVICE_PROVIDER_BY_KEY_QUERY = `
  query ServiceProviderByKey($serviceKey: String!) {
    serviceProviderByKey(serviceKey: $serviceKey) {
      id
      serviceProviderName
      serviceKey
      contactEmail
      contactPhone
      primaryColor
      logoUrl
      isActive
      maxDistance
      defaultLocation {
        lat
        lng
      }
      defaultZoom
      timezone
      attributesJson
      createdAt
      updatedAt
    }
  }
`;

// ============================================
// Service Provider Mutations (Admin)
// ============================================

export const CREATE_SERVICE_PROVIDER_MUTATION = `
  mutation CreateServiceProvider($input: CreateServiceProviderInput!) {
    createServiceProvider(input: $input) {
      serviceProvider {
        id
        serviceProviderName
        serviceKey
        contactEmail
        contactPhone
        primaryColor
        logoUrl
        isActive
        maxDistance
        defaultLocation {
          lat
          lng
        }
        defaultZoom
        timezone
        attributesJson
        createdAt
        updatedAt
      }
      success
      message
    }
  }
`;

export const UPDATE_SERVICE_PROVIDER_MUTATION = `
  mutation UpdateServiceProvider($input: UpdateServiceProviderInput!) {
    updateServiceProvider(input: $input) {
      serviceProvider {
        id
        serviceProviderName
        serviceKey
        contactEmail
        contactPhone
        primaryColor
        logoUrl
        isActive
        maxDistance
        defaultLocation {
          lat
          lng
        }
        defaultZoom
        timezone
        attributesJson
        createdAt
        updatedAt
      }
      success
      message
    }
  }
`;

export const DELETE_SERVICE_PROVIDER_MUTATION = `
  mutation DeleteServiceProvider($id: UUID!) {
    deleteServiceProvider(id: $id) {
      serviceProvider {
        id
        serviceKey
        isActive
      }
      success
      message
    }
  }
`;

// ============================================
// Contact & Feedback Mutations
// ============================================

export const SUBMIT_CONTACT_MUTATION = `
  mutation SubmitContact($input: ContactInput!) {
    submitContact(input: $input) {
      contact {
        id
        name
        email
        message
        createdAt
      }
    }
  }
`;

export const SUBMIT_FEEDBACK_MUTATION = `
  mutation SubmitFeedback($input: FeedbackInput!) {
    submitFeedback(input: $input) {
      feedback {
        id
        rating
        feedbackText
        createdAt
      }
    }
  }
`;

// ============================================
// User Management Mutations (Admin)
// ============================================

export const CREATE_SERVICE_PROVIDER_ADMIN_MUTATION = `
  mutation CreateServiceProviderAdmin($input: CreateUserInput!) {
    createServiceProviderAdmin(input: $input) {
      user {
        id
        email
        fullName
        role
        serviceProviderId
      }
    }
  }
`;

export const UPDATE_USER_PROFILE_MUTATION = `
  mutation UpdateUserProfile($input: UpdateUserInput!) {
    updateUserProfile(input: $input) {
      user {
        id
        email
        fullName
        phoneNumber
        updatedAt
      }
    }
  }
`;

export const UPDATE_USER_ROLE_MUTATION = `
  mutation UpdateUserRole($input: UpdateUserRoleInput!) {
    updateUserRole(input: $input) {
      user {
        id
        email
        fullName
        role
        phoneNumber
        serviceProviderId
        isActive
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_USER_STATUS_MUTATION = `
  mutation UpdateUserStatus($input: UpdateUserStatusInput!) {
    updateUserStatus(input: $input) {
      user {
        id
        email
        fullName
        role
        phoneNumber
        serviceProviderId
        isActive
        createdAt
        updatedAt
      }
    }
  }
`;

export const ASSIGN_USER_TO_SERVICE_PROVIDER_MUTATION = `
  mutation AssignUserToServiceProvider($input: AssignUserToServiceProviderInput!) {
    assignUserToServiceProvider(input: $input) {
      user {
        id
        email
        fullName
        role
        phoneNumber
        serviceProviderId
        isActive
        createdAt
        updatedAt
      }
    }
  }
`;
